/**
 * The form on a website page, standard look (mockup 1.1, one step): the studio's questions through the app's
 * FormRenderer with each question's pieces as parts, then the visitor's details (the Telegram offer and the
 * guest fields, or who they are signed in as), Send with the note and Privacy, and the thank-you. With two
 * steps (Dana's drum request), Next under the questions, then Back above the details. Draws the behavior
 * half; holds no rule. No title: Telify's page heads it with the form's name.
 *
 * site-form-buy-mode: placed with a product, one step, no guest fields, and at none left only Sold out.
 */
import { Fragment, useEffect, useRef } from 'react';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { EmailField } from '@/components/forms/EmailField';
import { TextField } from '@/components/forms/TextField';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { SiteFormRuntime } from './useSiteForm';
import css from './SiteForm.module.css';
import { contract } from './contract';

const part = partsOf(contract);

/** A website form sends once, never on blur. */
function noSave() {}

/** The two fields Telify adds for a guest (D6). */
const GUEST_NAME = { fieldId: 'site-form-guest-name', type: 'TEXT', label: 'Your name', required: true, isPII: false } as const;
const GUEST_EMAIL = { fieldId: 'site-form-guest-email', type: 'EMAIL', label: 'Email', required: true, isPII: false } as const;

export function SiteFormView(runtime: SiteFormRuntime) {
  const { state, actions, options, form, words } = runtime;
  const { status, step } = state;
  const twoSteps = options.steps === 'two' && !state.buying;

  // A step change starts at its top: the details heading, or the first question after Back.
  const rootRef = useRef<HTMLDivElement>(null);
  const shownStep = useRef(step);
  useEffect(() => {
    if (shownStep.current === step) return;
    shownStep.current = step;
    const root = rootRef.current;
    root?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    const target = step === 'details' ? root?.querySelector<HTMLElement>('[data-step-start]') : root?.querySelector<HTMLElement>('input, textarea, select');
    target?.focus({ preventScroll: true });
  }, [step]);

  // Sent, the form gives way to a short confirmation: the page brings it into view, never leaving the visitor scrolled
  // past it where Send was (card 4 walk, Gabriel 2026-11-01).
  const doneRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const done = status === 'sent' ? doneRef.current : null;
    if (!done) return;
    const box = done.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) done.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [status]);

  if (status === 'unavailable') return null;
  if (status === 'loading') {
    return (
      <div className={css.loading} role="status" {...pieceRoot(contract)} {...part('form')}>
        Loading form…
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={css.failed} {...pieceRoot(contract)} {...part('form')}>
        <p role="alert">Couldn&rsquo;t load the form.</p>
        <button type="button" className={`${shared.btn} ${shared.btnOutline}`} onClick={actions.retry}>
          Try again
        </button>
      </div>
    );
  }
  if (status === 'sent') {
    return (
      <section ref={doneRef} className={css.done} aria-live="polite" {...pieceRoot(contract)} {...part('done')}>
        <div className={css.orb} aria-hidden="true">
          <Icon name="check" />
        </div>
        <h2 className={css.doneHeading} {...part('doneHeading')}>
          {options.doneHeading}
        </h2>
        <p className={css.doneText} {...part('doneText')}>
          {words.doneText}
        </p>
        {state.openBot && runtime.botHref && (
          <p className={css.openBot} {...part('openBot')}>
            To hear back in Telegram, open{' '}
            <a href={runtime.botHref} target="_blank" rel="noopener noreferrer">
              {state.openBot}
            </a>{' '}
            and press Start.
          </p>
        )}
      </section>
    );
  }
  if (state.soldOut) {
    // None left: the questions are put away, and the button says so.
    return (
      <div className={css.piece} {...pieceRoot(contract)} {...part('form')}>
        <div className={css.bar}>
          <button type="button" className={`${shared.btn} ${shared.btnPrimary} ${css.send}`} disabled {...part('send', 'soldOut')}>
            Sold out
          </button>
        </div>
      </div>
    );
  }
  if (!form) return null;

  const sending = status === 'sending';
  const onQuestions = twoSteps && step === 'questions';
  return (
    <div ref={rootRef} className={css.piece} {...pieceRoot(contract)} {...part('form')}>
      {state.changed && (
        <p className={css.changed} role="status" {...part('changed')}>
          This form was updated. Check your answers and send again.
        </p>
      )}
      <FormRenderer
        key={runtime.formKey}
        form={form}
        markParts
        fieldsAttributes={part('questions')}
        fieldsHidden={twoSteps && step === 'details'}
        onSubmit={onQuestions ? actions.next : actions.send}
        onSave={noSave}
        isSubmitting={sending}
        serverErrors={runtime.serverErrors}
        disableAutoSave
        appearance="clientBooking"
        renderActions={() =>
          onQuestions ? (
            <div className={css.bar}>
              <button type="submit" className={`${shared.btn} ${shared.btnPrimary} ${css.send}`} {...part('next')}>
                {options.nextLabel}
                <Icon name="arrow" />
              </button>
            </div>
          ) : (
            <>
              {twoSteps && (
                <button type="button" className={css.back} onClick={actions.back} disabled={sending} {...part('back')}>
                  <Icon name="back" small />
                  Back
                </button>
              )}
              {runtime.sessionKnown && <Details {...runtime} />}
              {/* D7: a person never fills it; it is off-screen and out of the tab order. */}
              <input ref={runtime.trapRef} className={css.trap} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className={css.bar}>
                {state.error && !sending && (
                  <p className={css.error} role="alert" {...part('error')}>
                    {state.error}
                  </p>
                )}
                <button type="submit" className={`${shared.btn} ${shared.btnPrimary} ${css.send}`} disabled={sending} {...part('send', sending && 'disabled')}>
                  {sending ? 'Sending…' : options.sendLabel}
                  {!sending && <Icon name="arrow" />}
                </button>
                <p className={css.note} {...part('note')}>
                  {words.note}{' '}
                  <a href={runtime.privacyHref} target="_blank" rel="noopener" {...part('privacy')}>
                    Privacy
                  </a>
                </p>
              </div>
            </>
          )
        }
      />
    </div>
  );
}

/** The visitor's details: the offer and the guest fields, or who they are signed in as. */
function Details({ state, actions, options, words, signIn }: SiteFormRuntime) {
  const ownStep = options.steps === 'two' && !state.buying;
  return (
    <section className={`${css.details} ${ownStep ? css.detailsStep : ''}`} {...part('details')}>
      <h2 className={css.detailsHeading} tabIndex={ownStep ? -1 : undefined} data-step-start={ownStep ? '' : undefined} {...part('detailsHeading')}>
        {words.detailsHeading}
      </h2>
      {state.signedInAs ? (
        <p className={css.signedIn} {...part('signedIn')}>
          <Icon name="tg" small />
          <span>
            {words.signedInLine.split('{name}').map((text, i) => (
              <Fragment key={i}>
                {i > 0 && <b>{state.signedInAs}</b>}
                {text}
              </Fragment>
            ))}
          </span>
        </p>
      ) : (
        // One block, as mockup 1.1 draws it: the "or" line and the fields keep their own spacing, not the section's gap.
        <div>
          <div className={css.offer} {...part('offer')}>
            <p className={css.offerHeading}>{words.offer.heading}</p>
            {words.offer.lead && <p className={css.offerLead}>{words.offer.lead}</p>}
            <ul className={css.benefits}>
              {words.offer.benefits.map((b, i) => (
                <li key={i}>
                  <Icon name="check" small className={css.tick} />
                  <span>
                    {b.title && <b>{b.title} </b>}
                    {b.line}
                  </span>
                </li>
              ))}
            </ul>
            <button type="button" className={css.signIn} onClick={actions.signIn} disabled={!signIn.ready || signIn.pending} {...part('offerSignIn')}>
              <Icon name="tg" small />
              Sign in with Telegram
            </button>
            {actions.signInAsTestAccount && (
              <button type="button" className={css.signIn} onClick={actions.signInAsTestAccount} disabled={signIn.pending} {...part('offerTestAccount')}>
                Sign in as test account
              </button>
            )}
            {signIn.failed && (
              <p className={css.signInFailed} role="alert">
                {state.buying ? 'Couldn’t sign in with Telegram. Please try again.' : 'Couldn’t sign in with Telegram. Please try again, or send as a guest.'}
              </p>
            )}
          </div>
          {/* Buying asks no name or email here: checkout does. */}
          {!state.buying && (
            <>
              <div className={css.or} {...part('or')}>
                <span>or send it as a guest</span>
              </div>
              {/* The app's own fields, so the two Telify adds look like the questions above them. */}
              <div {...part('guestName')}>
                <TextField field={GUEST_NAME} value={state.guestName} onChange={actions.setGuestName} onBlur={noSave} error={state.guestNameError ?? undefined} />
              </div>
              <div {...part('guestEmail')}>
                <EmailField field={GUEST_EMAIL} value={state.guestEmail} onChange={actions.setGuestEmail} onBlur={noSave} error={state.guestEmailError ?? undefined} />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

